import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const STORAGE_KEY = "msh_token";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = unknown / not authed
    const [loading, setLoading] = useState(true);

    const setAuthHeader = (token) => {
        if (token) {
            api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            localStorage.setItem(STORAGE_KEY, token);
        } else {
            delete api.defaults.headers.common["Authorization"];
            localStorage.removeItem(STORAGE_KEY);
        }
    };

    const bootstrap = useCallback(async () => {
        // Isolated system tab: its own client owns the employee session.
        // Include the retired route so its redirect cannot disturb admin login.
        if (typeof window !== "undefined" && ["/demo", "/system/medarbejder"].includes(window.location.pathname.replace(/\/$/, ""))) {
            setLoading(false);
            return;
        }
        // CRITICAL: If returning from Google OAuth callback, skip the /auth/me check.
        // AuthCallback will exchange the session_id and set the JWT first.
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const token = localStorage.getItem(STORAGE_KEY);
        if (!token) {
            setLoading(false);
            return;
        }
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            setAuthHeader(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { bootstrap(); }, [bootstrap]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setAuthHeader(data.token);
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        setAuthHeader(data.token);
        setUser(data.user);
        return data.user;
    };

    const acceptInvite = async (payload) => {
        const { data } = await api.post("/invitations/accept", payload);
        setAuthHeader(data.token);
        setUser(data.user);
        return data.user;
    };

    // Google Sign-in: exchange session_id (from URL fragment) for our JWT.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const loginWithGoogleSession = async (sessionId) => {
        const { data } = await api.post("/auth/google-session", { session_id: sessionId });
        setAuthHeader(data.token);
        setUser(data.user);
        return { user: data.user, created: !!data.created };
    };

    const logout = () => {
        setAuthHeader(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, acceptInvite, loginWithGoogleSession }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
